<template>
  <div class="auth-page">
    <div class="auth-container">
      <div class="auth-logo">
        <a href="/">
          <img :src="logoUrl" alt="Ibomstore" />
        </a>
      </div>

      <div class="auth-card">
        <div class="auth-header">
          <h1>Complete Your Profile</h1>
          <p>Add your address information to continue</p>
        </div>

        <form @submit.prevent="handleSubmit" class="auth-form">
          <div class="form-group">
            <label>Phone Number *</label>
            <input
              v-model="form.mobile"
              type="tel"
              placeholder="Enter your phone number"
              required
            >
            <span v-if="errors.mobile" class="error">{{ errors.mobile[0] }}</span>
          </div>

          <div class="form-group">
            <label>Address *</label>
            <input
              v-model="form.address"
              type="text"
              placeholder="Enter your street address"
              required
            >
            <span v-if="errors.address" class="error">{{ errors.address[0] }}</span>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>City *</label>
              <input
                v-model="form.city"
                type="text"
                placeholder="Enter your city"
                required
              >
              <span v-if="errors.city" class="error">{{ errors.city[0] }}</span>
            </div>
            <div class="form-group">
              <label>ZIP Code *</label>
              <input
                v-model="form.zip"
                type="text"
                placeholder="ZIP code"
                required
              >
              <span v-if="errors.zip" class="error">{{ errors.zip[0] }}</span>
            </div>
          </div>

          <div class="form-group">
            <label>State *</label>
            <select v-model="form.state_id" required>
              <option value="">Select your state</option>
              <option v-for="state in states" :key="state.id" :value="state.id">{{ state.name }}</option>
            </select>
            <span v-if="errors.state_id" class="error">{{ errors.state_id[0] }}</span>
          </div>

          <div class="form-group">
            <label>Gender *</label>
            <select v-model="form.gender" required>
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            <span v-if="errors.gender" class="error">{{ errors.gender[0] }}</span>
          </div>

          <div class="form-group">
            <label>Date of Birth *</label>
            <div class="dob-row">
              <select v-model="dob.day" required>
                <option value="">Day</option>
                <option v-for="d in 31" :key="d" :value="d">{{ d }}</option>
              </select>
              <select v-model="dob.month" required>
                <option value="">Month</option>
                <option v-for="(m, i) in months" :key="i" :value="i + 1">{{ m }}</option>
              </select>
              <select v-model="dob.year" required>
                <option value="">Year</option>
                <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
              </select>
            </div>
            <span v-if="errors.dob" class="error">{{ errors.dob[0] }}</span>
            <span v-if="dobError" class="error">{{ dobError }}</span>
          </div>

          <button type="submit" class="btn-submit" :disabled="loading">
            {{ loading ? 'Saving...' : 'Complete Profile' }}
          </button>

        </form>

        <div v-if="successMessage" class="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {{ successMessage }}
        </div>

        <div v-if="errorMessage" class="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AddressForm',
  props: {
    states: {
      type: Array,
      required: true
    },
    user: {
      type: Object,
      default: null
    }
  },
  data() {
    const currentYear = new Date().getFullYear()
    const maxYear = currentYear - 18
    const minYear = currentYear - 100
    const yearsList = []
    for (let y = maxYear; y >= minYear; y--) {
      yearsList.push(y)
    }

    const userDob = this.user?.dob ? new Date(this.user.dob) : null

    return {
      logoUrl: '/logos/logodark.svg',
      form: {
        mobile: this.user?.mobile || '',
        address: this.user?.address || '',
        city: this.user?.city || '',
        state_id: this.user?.state_id || '',
        zip: this.user?.zip || '',
        gender: this.user?.gender || '',
        dob: ''
      },
      dob: {
        day: userDob ? userDob.getDate() : '',
        month: userDob ? userDob.getMonth() + 1 : '',
        year: userDob ? userDob.getFullYear() : ''
      },
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      years: yearsList,
      dobError: '',
      errors: {},
      loading: false,
      successMessage: '',
      errorMessage: ''
    }
  },
  methods: {
    async handleSubmit() {
      this.loading = true
      this.errors = {}
      this.errorMessage = ''
      this.successMessage = ''
      this.dobError = ''

      if (!this.dob.day || !this.dob.month || !this.dob.year) {
        this.dobError = 'Please select your complete date of birth'
        this.loading = false
        return
      }

      const month = String(this.dob.month).padStart(2, '0')
      const day = String(this.dob.day).padStart(2, '0')
      this.form.dob = `${this.dob.year}-${month}-${day}`

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

        const response = await fetch('/onboarding/address', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'Accept': 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify(this.form)
        })

        const data = await response.json()

        if (response.ok) {
          this.successMessage = 'Profile completed! Redirecting...'
          setTimeout(() => {
            window.location.href = data.redirect || '/'
          }, 500)
        } else {
          this.errorMessage = data.message || 'Failed to save address'
          if (data.errors) {
            this.errors = data.errors
          }
        }
      } catch (error) {
        this.errorMessage = 'An error occurred. Please try again.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

